import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { rememberReturnTo, takeReturnTo } from '../lib/returnTo';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Motif } from '../components/ui/Motif';
import { CountrySelect } from '../components/CountrySelect';
import { toast } from 'sonner';

const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.uk', 'yahoo.co.in', 'ymail.com', 'rocketmail.com',
  'outlook.com', 'hotmail.com', 'hotmail.co.uk', 'live.com', 'msn.com', 'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'aim.com', 'proton.me', 'protonmail.com', 'pm.me', 'gmx.com', 'gmx.net', 'gmx.de',
  'mail.com', 'email.com', 'zoho.com', 'zohomail.com', 'yandex.com', 'yandex.ru',
]);
const isFreeEmail = (e) => {
  const at = (e || '').trim().toLowerCase().split('@');
  return at.length === 2 && FREE_EMAIL_DOMAINS.has(at[1]);
};

const PLAN_LABELS = { starter_monthly: 'Starter · Monthly', starter_annual: 'Starter · Annual', professional_monthly: 'Growth · Monthly', professional_annual: 'Growth · Annual' };

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan');
  const planLabel = PLAN_LABELS[selectedPlan] || 'Starter';
  const [step, setStep] = useState(searchParams.get('mode') === 'register' ? 'register' : 'email');
  const [signupRole, setSignupRole] = useState('recruiter');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [isNewAccount, setIsNewAccount] = useState(false);
  const submittedCode = useRef('');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Remember (or forget) the page to return to; also read by the emailed sign-in link's tab.
  useEffect(() => { rememberReturnTo(searchParams.get('next')); }, [searchParams]);

  const getRedirectPath = useCallback((role) => {
    const redirect = searchParams.get('redirect');
    const plan = searchParams.get('plan');
    const back = takeReturnTo();
    if (redirect === 'payment' && plan && role !== 'candidate') return `/payment?plan=${plan}`;
    return back || (role === 'candidate' ? '/portal' : '/dashboard');
  }, [searchParams]);

  const sendCode = async (addr, { silent } = {}) => {
    const res = await authAPI.requestCode(addr);
    setCooldown(res.data?.resend_in_seconds ?? 60);
    if (!silent) toast.success(`We've emailed a 6-digit code to ${addr}`);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await sendCode(email.trim().toLowerCase());
      setIsNewAccount(false);
      setCode('');
      submittedCode.current = '';
      setStep('code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send a code. Please try again.');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      await sendCode(email.trim().toLowerCase());
      setCode('');
      submittedCode.current = '';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend the code.');
    } finally { setLoading(false); }
  };

  const verify = useCallback(async (value) => {
    setLoading(true);
    try {
      const res = await authAPI.verifyCode({ email: email.trim().toLowerCase(), code: value });
      const u = res.data.user;
      login(u);
      toast.success(res.data.is_new_user ? `Welcome to CHIRE, ${u.name}!` : `Welcome back, ${u.name}!`);
      navigate(getRedirectPath(u.role));
    } catch (err) {
      setCode('');
      submittedCode.current = '';
      toast.error(err.response?.data?.detail || 'That code did not work.');
    } finally { setLoading(false); }
  }, [email, login, navigate, getRedirectPath]);

  // Auto-submit the moment all six digits are in — no extra tap needed.
  useEffect(() => {
    if (step === 'code' && code.length === 6 && submittedCode.current !== code) {
      submittedCode.current = code;
      verify(code);
    }
  }, [code, step, verify]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!country) { toast.error('Country is required'); return; }
    setLoading(true);
    try {
      const res = await authAPI.register({
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        city: city.trim(),
        country: country.trim(),
        role: signupRole,
      });
      setCooldown(res.data?.resend_in_seconds ?? 60);
      setIsNewAccount(true);
      setCode('');
      submittedCode.current = '';
      setStep('code');
      toast.success(`Account created — we've emailed a 6-digit code to ${res.data.email}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // A 422 lists each invalid field — show the first ("First name is required").
      const msg = Array.isArray(detail) ? detail[0]?.msg?.replace(/^Value error, /, '') : detail;
      toast.error(msg || 'Registration failed');
    } finally { setLoading(false); }
  };

  const inputStyle = { width: '100%', padding: '0.625rem 0.875rem', borderRadius: 10, border: '1px solid #CBD5E1', background: '#FAF7F2', color: '#002A38', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: '0.375rem' };
  const linkStyle = { background: 'none', border: 'none', color: '#0076A3', cursor: 'pointer', fontSize: '0.8125rem', padding: 0 };

  const heading = step === 'register' ? 'Create your CHIRE account' : step === 'code' ? 'Enter your code' : 'Log in to CHIRE';
  const subheading = step === 'register'
    ? `Create a ${signupRole} account — no password needed`
    : step === 'code'
      ? `We sent a 6-digit code to ${email}`
      : "Enter your email and we'll send you a 6-digit code";

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F1EAE1', flexDirection: 'row', flexWrap: 'wrap' }}>
      {/* Left Hero - hidden on very small screens, shown as top banner on tablets */}
      <div style={{
        flex: '1 1 400px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: 'clamp(1.5rem, 4vw, 3rem)', position: 'relative', overflow: 'hidden', minHeight: '40vh',
        backgroundImage: 'url(https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)',
        backgroundSize: 'cover', backgroundPosition: 'center',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(155deg, rgba(28,25,23,0.88) 0%, rgba(28,25,23,0.6) 42%, rgba(240, 80, 0,0.72) 100%)' }} />
        <div className="grain-overlay" style={{ opacity: 0.16 }} />
        <Motif shape="star" className="motif-float" style={{ position: 'absolute', top: '9%', right: '8%', width: 44, color: '#FAD9A0', opacity: 0.85, zIndex: 1 }} />
        <Motif shape="ring" className="motif-float" stroke style={{ position: 'absolute', bottom: '10%', left: '6%', width: 56, color: '#A9DCF2', opacity: 0.5, zIndex: 1, animationDelay: '0.9s' }} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 540 }}>
          <div style={{ marginBottom: '2.5rem' }}>
            <div style={{ display: 'inline-block', background: '#fff', borderRadius: 12, padding: '10px 16px' }}>
              <img src="/chire-logo-full.png" alt="CHIRE" style={{ height: 96, objectFit: 'contain' }} />
            </div>
            <p style={{ fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.6)', marginTop: '0.75rem' }}>
              Assessments &nbsp;|&nbsp; Interviews &nbsp;|&nbsp; Compliance
            </p>
          </div>
          <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: 'clamp(2.25rem, 4vw, 3rem)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: '1rem', color: '#fff' }}>Empower Your<br /><span style={{ color: '#FAD9A0' }}>Potential</span></h2>
          <p style={{ fontSize: '1.125rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.6, marginBottom: '2.5rem' }}>All-in-One Recruit Platform</p>
          <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap' }}>
            {[{ val: 'Explainable', lbl: 'AI Scoring' }, { val: 'Bias-free', lbl: 'Evaluation' }, { val: 'AI', lbl: 'Powered Interviews' }, { val: 'Secure', lbl: 'Compliance' }].map(({ val, lbl }) => (
              <div key={lbl}>
                <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 800, color: '#FAD9A0' }}>{val}</div>
                <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.125rem' }}>{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form */}
      <div style={{ flex: '1 1 320px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(1rem, 3vw, 2rem)', background: '#fff', minWidth: 0 }}>
        <Card className="w-full" style={{ background: '#fff', border: 'none', borderRadius: 14, padding: 'clamp(1.25rem, 3vw, 2rem)', boxShadow: 'none', maxWidth: 400, width: '100%' }}>

          {/* Back to Home */}
          <button data-testid="back-home-btn" onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', background: 'none', border: 'none', color: '#0076A3', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500, padding: 0, marginBottom: '1.25rem' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Back to Home
          </button>

          {/* Role Toggle — signup only (on sign-in the role comes from the account) */}
          {step === 'register' && (
            <div style={{ display: 'flex', background: '#F0EBE3', borderRadius: 10, padding: '0.25rem', marginBottom: '1.5rem' }}>
              {['recruiter', 'candidate'].map(r => (
                <button key={r} data-testid={`signup-role-${r}`} onClick={() => setSignupRole(r)} style={{
                  flex: 1, padding: '0.625rem', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: '0.8125rem', transition: 'background-color 0.2s, color 0.2s', textTransform: 'capitalize',
                  background: signupRole === r ? '#0076A3' : 'transparent',
                  color: signupRole === r ? '#fff' : '#64748B',
                }}>{r}</button>
              ))}
            </div>
          )}

          {/* Header */}
          <div style={{ marginBottom: '1.25rem' }}>
            <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.375rem', color: '#002A38' }}>{heading}</h1>
            <p style={{ fontSize: '0.875rem', color: '#64748B' }}>{subheading}</p>
          </div>

          {selectedPlan && (
            <div data-testid="selected-plan-banner" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: '#FFE7D9', border: '1px solid #BBE3F5', borderRadius: 10, padding: '0.7rem 0.9rem', marginBottom: '1.25rem' }}>
              <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 999, background: '#0076A3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              </span>
              <span style={{ fontSize: '0.8125rem', color: '#7A2E22', lineHeight: 1.45 }}>You're subscribing to the <strong>{planLabel}</strong> plan — {step === 'register' ? 'create your account to continue to checkout.' : 'sign in to continue to checkout.'}</span>
            </div>
          )}

          {/* STEP 1 — EMAIL */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} data-testid="request-code-form">
              <div>
                <label style={labelStyle}>Email</label>
                <input data-testid="login-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required autoFocus style={inputStyle} />
              </div>
              <p style={{ fontSize: '0.75rem', color: '#64748B', lineHeight: 1.5 }}>
                No password to remember. We'll email you a 6-digit code that works once and expires in 5 minutes.
              </p>
              <Button data-testid="send-code-button" type="submit" disabled={loading}
                style={{ width: '100%', marginTop: '0.25rem', padding: '0.75rem', borderRadius: 10, fontWeight: 600, background: 'linear-gradient(135deg, #00A0E0 0%, #F05000 100%)', color: '#fff' }}>
                {loading ? 'Sending code...' : 'Email me a code'}
              </Button>
            </form>
          )}

          {/* STEP 2 — CODE */}
          {step === 'code' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} data-testid="verify-code-form">
              <div>
                <label style={labelStyle}>6-digit code</label>
                <input
                  data-testid="otp-code-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  autoFocus
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="······"
                  style={{ ...inputStyle, textAlign: 'center', fontSize: '1.5rem', fontWeight: 700, letterSpacing: '0.5em', padding: '0.875rem 0.5rem 0.875rem 1rem', fontFamily: 'monospace' }}
                />
              </div>

              <Button data-testid="verify-code-button" type="button" disabled={loading || code.length !== 6} onClick={() => verify(code)}
                style={{ width: '100%', padding: '0.75rem', borderRadius: 10, fontWeight: 600, background: '#0076A3', color: '#fff' }}>
                {loading ? 'Verifying...' : isNewAccount ? 'Verify & finish signup' : 'Sign in'}
              </Button>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button data-testid="change-email-btn" type="button" onClick={() => { setStep(isNewAccount ? 'register' : 'email'); setCode(''); }} style={linkStyle}>
                  ← Change email
                </button>
              </div>

              <div data-testid="otp-help" style={{ marginTop: '0.25rem', padding: '0.8rem 0.95rem', background: '#F1F6F9', border: '1px solid #E2ECF1', borderRadius: 10, textAlign: 'center' }}>
                <p style={{ fontSize: '0.8125rem', color: '#1E293B', fontWeight: 700, margin: 0 }}>Didn't get the code?</p>
                <p style={{ fontSize: '0.75rem', color: '#64748B', lineHeight: 1.55, margin: '0.3rem 0 0.6rem' }}>
                  It can take a minute to arrive. Check your <strong>spam</strong> or <strong>promotions</strong> folder — or use the one-tap sign-in button inside the email.
                </p>
                <button data-testid="resend-code-btn" type="button" onClick={handleResend} disabled={cooldown > 0 || loading}
                  style={{ ...linkStyle, fontWeight: 700, color: cooldown > 0 ? '#94A3B8' : '#0076A3', cursor: cooldown > 0 ? 'default' : 'pointer' }}>
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — REGISTER */}
          {step === 'register' && (
            <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} data-testid="register-form">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={labelStyle}>First Name</label>
                  <input data-testid="register-first-name-input" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="John" required maxLength={100} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Last Name</label>
                  <input data-testid="register-last-name-input" type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Doe" required maxLength={100} style={inputStyle} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={labelStyle}>City</label>
                  <input data-testid="register-city-input" type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="Hyderabad" required maxLength={100} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Country</label>
                  <CountrySelect testId="register-country-select" value={country} onChange={setCountry} style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input data-testid="register-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required style={inputStyle} />
                {signupRole === 'recruiter' && isFreeEmail(email) && (
                  <p data-testid="work-email-hint" style={{ fontSize: '0.6875rem', color: '#B45309', marginTop: '0.375rem', lineHeight: 1.5, display: 'flex', gap: '0.375rem' }}>
                    <span aria-hidden="true">⚠</span>
                    <span>Recruiters need a <strong>work email</strong>. Public providers (Gmail, Outlook…) aren't accepted for employer accounts.</span>
                  </p>
                )}
              </div>
              <p style={{ fontSize: '0.75rem', color: '#64748B', lineHeight: 1.5 }}>
                We'll email a 6-digit code to confirm this address. There's no password to create.
              </p>
              <Button data-testid="register-submit-button" type="submit" disabled={loading}
                style={{ width: '100%', marginTop: '0.25rem', padding: '0.75rem', borderRadius: 10, fontWeight: 600, background: 'linear-gradient(135deg, #00A0E0 0%, #F05000 100%)', color: '#fff' }}>
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
          )}

          {/* Footer links */}
          <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
            {step === 'email' && (
              <>
                <button data-testid="go-to-register-btn" onClick={() => setStep('register')} style={linkStyle}>
                  Don't have an account? Sign up
                </button>
                <button data-testid="view-pricing-btn" onClick={() => navigate('/pricing')} style={{ ...linkStyle, color: '#718096' }}>
                  View Pricing
                </button>
              </>
            )}
            {step === 'register' && (
              <button data-testid="go-to-login-btn" onClick={() => setStep('email')} style={linkStyle}>
                Already have an account? Sign in
              </button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
